import React from "react"
import { Calendar, Check, Clock, Users } from "lucide-mui"
import { DateTime } from "luxon"
import i18n from "@/i18n"
import { formatDuration } from "@/shared/datetime"
import { type OpeningHour } from "@/utils/opening_hours"
import DateStep from "./reservation_booking_steps/date_step"
import GuestsStep from "./reservation_booking_steps/guests_step"
import TimeStep from "./reservation_booking_steps/time_step"
import { useReservationBookingFlow } from "./use_reservation_booking_flow"

interface ReservationSidebarProps {
  onReserveClick: (date: DateTime | null, peopleAmount: number) => void
  cancellationDeadline?: number | null
  venueSlug: string
  timezone: string
  country: string
  openingHours?: OpeningHour[]
  minAdvanceBookingTime?: number | null
  minReservationTime?: number | null
  tables?: Array<{ id: string; minCapacity?: number | null; maxCapacity?: number | null }>
}

type ReservationSidebarStep = "date" | "guests" | "time"

const steps = [
  { id: "date" as const, label: i18n.t("app.public.venue.sidebar.date"), icon: Calendar },
  { id: "guests" as const, label: i18n.t("app.public.venue.sidebar.guests"), icon: Users },
  { id: "time" as const, label: i18n.t("app.public.venue.sidebar.time"), icon: Clock },
]

const formatStepSummary = (
  stepId: ReservationSidebarStep,
  activeStep: ReservationSidebarStep,
  date: DateTime | null,
  peopleAmount: number,
  hasValidGuestSelection: boolean,
) => {
  if (stepId === "date" && activeStep !== "date" && date?.isValid) {
    return date.toFormat("dd.MM")
  }

  if (stepId === "guests" && activeStep === "time" && hasValidGuestSelection) {
    return String(peopleAmount)
  }

  return null
}

const ReservationSidebar: React.FC<ReservationSidebarProps> = ({
  onReserveClick,
  cancellationDeadline,
  venueSlug,
  timezone,
  country,
  openingHours,
  minAdvanceBookingTime,
  minReservationTime,
  tables,
}) => {
  const {
    values,
    activeStep,
    hasValidGuestSelection,
    effectiveConfirmedPeopleAmount,
    timeSelectionConfirmed,
    availability,
    availabilityError,
    refetch,
    handleStepClick,
    handleDateChange,
    handlePeopleAmountChange,
    handleTimeSelect,
  } = useReservationBookingFlow({
    venueSlug,
    timezone,
    openingHours,
    minAdvanceBookingTime,
    minReservationTime,
    tables,
  })
  const [activePanelHeight, setActivePanelHeight] = React.useState<number | null>(null)
  const panelRefs = React.useRef<Record<ReservationSidebarStep, HTMLDivElement | null>>({
    date: null,
    guests: null,
    time: null,
  })
  const activeStepIndex = steps.findIndex((step) => step.id === activeStep)

  React.useLayoutEffect(() => {
    const activePanel = panelRefs.current[activeStep]
    if (!activePanel) return

    const updateHeight = () => setActivePanelHeight(activePanel.scrollHeight)

    updateHeight()

    if (typeof ResizeObserver === "undefined") {
      return
    }

    const observer = new ResizeObserver(() => updateHeight())
    observer.observe(activePanel)

    return () => observer.disconnect()
  }, [activeStep, availability.timeSlots.length, availability.availableGuestCounts.length])

  const benefits = [
    i18n.t("app.public.venue.sidebar.no_phone_calls"),
    i18n.t("app.public.venue.sidebar.real_time_table_availability"),
    i18n.t("app.public.venue.sidebar.pick_exact_table"),
    i18n.t("app.public.venue.sidebar.instant_booking_confirmation"),
  ]

  return (
    <div className="w-full max-w-[300px] space-y-5">
      <div
        className="rounded-[10px] border border-[#e5e7eb] bg-[#fafafa] p-5 shadow-[2px_2px_10px_0_rgba(0,0,0,0.05)]"
        data-testid="public-venue-sidebar-container"
      >
        <div
          className="overflow-hidden rounded-[5px] border border-[#e5e7eb]"
          aria-label={i18n.t("app.public.venue.sidebar.booking_steps")}
        >
          <ol className="grid grid-cols-3">
            {steps.map((step) => {
              const isActive = step.id === activeStep
              const Icon = step.icon
              const stepButtonId = `public-venue-sidebar-step-button-${step.id}`
              const stepPanelId = `public-venue-sidebar-panel-${step.id}`
              const stepSummary = formatStepSummary(
                step.id,
                activeStep,
                values.date,
                values.peopleAmount,
                hasValidGuestSelection,
              )
              const buttonLabel = stepSummary ? `${step.label} ${stepSummary}` : step.label

              return (
                <li key={step.id} className="list-none border-r border-[#e5e7eb] last:border-r-0">
                  <button
                    id={stepButtonId}
                    type="button"
                    aria-label={buttonLabel}
                    aria-controls={stepPanelId}
                    aria-current={isActive ? "step" : undefined}
                    onClick={() => handleStepClick(step.id)}
                    className={`flex h-[26px] w-full items-center justify-center gap-1.5 text-[#374151] transition-colors ${
                      isActive ? "bg-[#f4f6f8]" : "bg-white"
                    }`}
                    data-testid={`public-venue-sidebar-step-${step.id}`}
                  >
                    <Icon className="h-[13px] w-[13px]" />
                    <span className={isActive || stepSummary ? "text-[12px] font-medium leading-4" : "sr-only"}>
                      {stepSummary || step.label}
                    </span>
                    {stepSummary && <span className="sr-only">{step.label}</span>}
                  </button>
                </li>
              )
            })}
          </ol>
        </div>

        <div
          className="mt-4 overflow-hidden transition-[height] duration-300 ease-out"
          style={activePanelHeight ? { height: `${activePanelHeight}px` } : undefined}
        >
          <div
            className="flex items-start transition-transform duration-300 ease-out"
            style={{ transform: `translateX(-${activeStepIndex * 100}%)` }}
            data-testid="public-venue-sidebar-step-panels"
          >
            <div
              ref={(element) => {
                panelRefs.current.date = element
              }}
              className={`w-full shrink-0 ${activeStep === "date" ? "visible" : "pointer-events-none invisible"}`}
              id="public-venue-sidebar-panel-date"
              aria-labelledby="public-venue-sidebar-step-button-date"
              aria-hidden={activeStep !== "date"}
            >
              <DateStep
                date={values.date}
                timezone={timezone}
                country={country}
                openingHours={openingHours}
                onDateChange={(date) => void handleDateChange(date)}
                onContinue={() => handleStepClick("guests")}
              />
            </div>

            <div
              ref={(element) => {
                panelRefs.current.guests = element
              }}
              className={`w-full shrink-0 ${activeStep === "guests" ? "visible" : "pointer-events-none invisible"}`}
              id="public-venue-sidebar-panel-guests"
              aria-labelledby="public-venue-sidebar-step-button-guests"
              aria-hidden={activeStep !== "guests"}
            >
              <GuestsStep
                selectedPeopleAmount={effectiveConfirmedPeopleAmount}
                availableGuestCounts={availability.availableGuestCounts}
                onPeopleAmountChange={(peopleAmount) => void handlePeopleAmountChange(peopleAmount)}
                onContinue={() => handleStepClick("time")}
              />
            </div>

            <div
              ref={(element) => {
                panelRefs.current.time = element
              }}
              className={`w-full shrink-0 ${activeStep === "time" ? "visible" : "pointer-events-none invisible"}`}
              id="public-venue-sidebar-panel-time"
              aria-labelledby="public-venue-sidebar-step-button-time"
              aria-hidden={activeStep !== "time"}
            >
              <TimeStep
                startTime={values.startTime}
                hasConfirmedSelection={Boolean(values.startTime) || timeSelectionConfirmed}
                timeSlots={availability.timeSlots}
                onTimeSelect={(startTime) => void handleTimeSelect(startTime)}
                onContinue={() => onReserveClick(values.date, values.peopleAmount)}
              />
            </div>
          </div>
        </div>

        {availabilityError && activeStep !== "date" && (
          <div className="mt-3 flex items-center justify-between gap-3 rounded-[8px] border border-[#f0d4d4] bg-[#fff7f7] px-3 py-2 text-xs text-[#9a4f4f]">
            <span>{i18n.t("app.public.venue.sidebar.availability_error")}</span>
            <button
              type="button"
              onClick={() => refetch()}
              className="font-medium text-[#2596fa]"
              data-testid="public-venue-sidebar-availability-retry"
            >
              {i18n.t("app.public.venue.sidebar.retry")}
            </button>
          </div>
        )}
      </div>

      <div
        className="space-y-[10px] px-5 text-[12px] leading-[15px] text-[#5f6774]"
        data-testid="public-venue-sidebar-benefits"
      >
        {benefits.map((benefit) => (
          <div key={benefit} className="flex items-start gap-[10px]">
            <Check className="mt-[3px] h-[11px] w-[11px] text-[#2596fa]" strokeWidth={2.5} />
            <span>{benefit}</span>
          </div>
        ))}

        {cancellationDeadline && cancellationDeadline > 0 && (
          <div className="flex items-start gap-[10px]">
            <Check className="mt-[3px] h-[11px] w-[11px] text-[#2596fa]" strokeWidth={2.5} />
            <span>
              {i18n.t("app.public.venue.sidebar.free_cancellation", {
                time: formatDuration(cancellationDeadline),
              })}
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

export default ReservationSidebar
